import { useState } from 'react';
import {
  ArrowsRightLeftIcon, ArrowPathIcon, ChevronRightIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useVanTransfers, useTransferLines } from '../hooks/useVanTransfers';

// Stock handed from one engineer's van to another.
//
// The receiver logs it, the giver accepts, and only then does anything move.
// That leaves one failure mode nobody outside the two engineers can see: a
// giver who never answers. The receiver is holding stock the system does not
// credit them with, their next order will be wrong, and no one is watching —
// which is why pending sits at the top of this screen and anything over three
// days old is flagged.
//
// Read-only. Whether logistics should be able to force a transfer through is a
// real decision, and better taken once there is evidence of what goes wrong.

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB',
  { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

const qty = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
};

const STATUS = {
  pending: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-rose-100 text-rose-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

function Lines({ id }) {
  const { data: lines, isLoading } = useTransferLines(id);
  if (isLoading) return <p className="px-4 py-3 text-sm text-gray-500">Loading items…</p>;
  if (!lines?.length) return <p className="px-4 py-3 text-sm text-gray-500">No items recorded.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
          <th className="px-4 py-2 font-medium">Item</th>
          <th className="px-4 py-2 text-right font-medium">Quantity</th>
          <th className="px-4 py-2 text-right font-medium">On the giving van when logged</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id} className="border-b border-gray-100">
            <td className="px-4 py-2 text-gray-900">
              {l.name}
              <span className="ml-2 text-xs text-gray-400">{l.item_code}</span>
            </td>
            <td className="px-4 py-2 text-right tabular-nums text-gray-900">{qty(l.quantity)}</td>
            {/* What both parties could see at the time. Derived van figures
                drift, so this is the honest record of what was believed. */}
            <td className="px-4 py-2 text-right tabular-nums text-gray-500">
              {l.from_van_on_hand_at_log == null ? '—' : qty(l.from_van_on_hand_at_log)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function VanTransfers() {
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useVanTransfers({ status });

  const transfers = data?.transfers || [];
  const counts = data?.counts || {};

  if (isError) {
    return (
      <div className="m-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        {error?.response?.data?.error || 'Could not load stock transfers.'}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ArrowsRightLeftIcon className="h-6 w-6 text-brand" />
            Stock transfers
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Stock handed between engineers&apos; vans. The receiver logs it and the giver confirms —
            nothing moves off a van until they do.
          </p>
        </div>
        <button
          type="button" onClick={() => refetch()}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {Number(counts.stale) > 0 ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-900">
            <strong>{counts.stale}</strong> transfer{Number(counts.stale) === 1 ? ' has' : 's have'} been
            waiting more than three days for the giving engineer to confirm. Until they do, the stock is
            on neither van&apos;s figures and the receiver&apos;s next order will be short.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {[
          ['', 'All'],
          ['pending', `Waiting (${counts.pending || 0})`],
          ['accepted', `Accepted (${counts.accepted || 0})`],
          ['declined', `Declined (${counts.declined || 0})`],
          ['cancelled', `Cancelled (${counts.cancelled || 0})`],
        ].map(([v, label]) => (
          <button
            key={v || 'all'} type="button" onClick={() => setStatus(v)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              status === v ? 'bg-brand text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : transfers.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No transfers to show.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 font-medium">Reference</th>
                  <th className="px-4 py-2 font-medium">From</th>
                  <th className="px-4 py-2 font-medium">To</th>
                  <th className="px-4 py-2 text-right font-medium">Items</th>
                  <th className="px-4 py-2 font-medium">Handed over</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => {
                  const stale = t.status === 'pending' && Number(t.age_hours) > 72;
                  return (
                    <>
                      <tr key={t.id} className={`border-b border-gray-100 ${stale ? 'bg-amber-50' : ''}`}>
                        <td className="px-4 py-2 font-medium text-gray-900">{t.reference}</td>
                        <td className="px-4 py-2 text-gray-700">
                          {t.from_name || '—'}
                          <span className="ml-2 text-xs tabular-nums text-gray-400">{t.from_van}</span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {t.to_name || '—'}
                          <span className="ml-2 text-xs tabular-nums text-gray-400">{t.to_van}</span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">{t.line_count}</td>
                        <td className="px-4 py-2 tabular-nums text-gray-700">{fmtDate(t.transferred_at)}</td>
                        <td className="px-4 py-2">
                          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${STATUS[t.status] || STATUS.cancelled}`}>
                            {t.status}
                          </span>
                          {stale ? (
                            <span className="ml-2 text-xs text-amber-700">
                              {Math.floor(Number(t.age_hours) / 24)}d waiting
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => setOpen(open === t.id ? null : t.id)}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"
                          >
                            Items
                            <ChevronRightIcon className={`h-4 w-4 transition-transform ${open === t.id ? 'rotate-90' : ''}`} />
                          </button>
                        </td>
                      </tr>
                      {open === t.id ? (
                        <tr key={`${t.id}-lines`} className="border-b border-gray-100 bg-gray-50">
                          <td colSpan={7} className="p-0">
                            <Lines id={t.id} />
                            {t.decline_reason ? (
                              <p className="px-4 py-2 text-sm text-rose-700">
                                Declined: {t.decline_reason}
                              </p>
                            ) : null}
                            {t.note ? (
                              <p className="px-4 py-2 text-sm italic text-gray-600">“{t.note}”</p>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
